import { useState } from 'react';
import type { ReactNode } from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import type { ImageSourcePropType } from 'react-native';

import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export type VideoPreparationProps = {
  title: string;
  actions: readonly { label: string; image: ImageSourcePropType; aspectRatio?: number }[];
  testID: string;
  inverse?: boolean;
  footer?: ReactNode;
};

export function VideoPreparation({
  title,
  actions,
  testID,
  inverse = false,
  footer,
}: VideoPreparationProps) {
  const theme = useTheme();
  const color = inverse ? theme.colors.textInverse : theme.colors.text;
  const [height, setHeight] = useState(560);
  const [titleHeight, setTitleHeight] = useState(48);
  const [labelHeights, setLabelHeights] = useState<Record<number, number>>({});
  const [footerHeight, setFooterHeight] = useState(0);
  const topPadding = theme.spacing(12);
  const bottomPadding = theme.spacing(14);
  const labelHeight = Math.max(0, ...actions.map((_, index) => labelHeights[index] ?? 32));
  const imageHeight = Math.max(24, Math.min(360,
    height - topPadding - bottomPadding - titleHeight - labelHeight - theme.spacing(4) - (footer ? footerHeight + theme.spacing(2) : 0),
  ));

  return (
    <ScrollView
      testID={testID}
      style={styles.container}
      onLayout={(event) => setHeight(event.nativeEvent.layout.height)}
      contentContainerStyle={[styles.content, { gap: theme.spacing(2), paddingHorizontal: theme.spacing(4), paddingTop: topPadding, paddingBottom: bottomPadding }]}
    >
      <Text variant={height < 320 ? 'body' : 'title'} weight="semibold" color={color} accessibilityRole="header" style={styles.centered} testID="incident-video-status-text" onLayout={(event) => setTitleHeight(event.nativeEvent.layout.height)}>
        {title}
      </Text>
      <View style={[styles.actions, { gap: theme.spacing(4) }]}>
        {actions.map((action, index) => (
          <View key={index} style={[styles.action, { gap: theme.spacing(2) }]}>
            <View style={{ height: labelHeight }}>
              <Text variant="label" color={color} style={styles.centered} onLayout={(event) => {
                const measuredHeight = event.nativeEvent.layout.height;
                setLabelHeights((previous) => previous[index] === measuredHeight ? previous : { ...previous, [index]: measuredHeight });
              }}>{action.label}</Text>
            </View>
            <Image source={action.image} accessibilityLabel={action.label} resizeMode="contain" style={[styles.image, { height: imageHeight, aspectRatio: action.aspectRatio ?? 965 / 1629 }]} />
          </View>
        ))}
      </View>
      {footer ? <View onLayout={(event) => setFooterHeight(event.nativeEvent.layout.height)}>{footer}</View> : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, width: '100%' },
  content: { flexGrow: 1, alignItems: 'center', justifyContent: 'center' },
  centered: { textAlign: 'center' },
  actions: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'center', width: '100%', maxWidth: 420 },
  action: { flex: 1, minWidth: 0, alignItems: 'center' },
  image: { maxWidth: '100%' },
});
